/**
 * Pure derivation functions for the Clients page.
 *
 * No React, no Apollo — typed inputs and outputs only. This is the
 * natural place to add tests later (simple input/output contract, no I/O).
 *
 * Server-side stat derivation (sessionsCount, lifetimeSpend, lastSessionAt,
 * nextSessionAt, primaryProvider) has been moved to the backend as of the
 * v2 paginated rewrite. Only timeline construction, label formatting,
 * and display helpers remain here.
 */

import { timeZoneAbbr } from "../utils/sessionTime";

// ── Input types (match the GraphQL response shapes) ─────────────────

export interface RawBooking {
  id: string;
  state: string;
  method: string;
  bookingTypeId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  bookingType: { id: string; name: string } | null;
  availability: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    providerId: string;
  } | null;
}

/** Inline tag shape returned by the API on Customer.tags. */
export interface CustomerTagInline {
  id: string;
  name: string;
  slug: string;
}

/** Shape returned by the paginated list query (no nested bookings). */
export interface ListCustomer {
  id: string;
  name: string;
  alias: string | null;
  email: string;
  phoneNumber: string | null;
  businessName: string | null;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  sessionsCount: number;
  lifetimeSpend: number;
  lastSessionAt: string | null;
  nextSessionAt: string | null;
  primaryProviderId: string | null;
  primaryProviderName: string | null;
  tags: CustomerTagInline[];
}

/** Shape returned by the detail query (includes nested bookings). */
export interface DetailCustomer extends ListCustomer {
  bookings: RawBooking[];
}

/** One of a person's Client memberships (client_people join, flattened). */
export interface MembershipInline {
  id: string;
  name: string;
  alias: string | null;
  status: string;
  isPrimary: boolean;
}

/** A person (contact-level identity) as returned by the People queries and
 *  nested inside a Client response. */
export interface PersonRow {
  id: string;
  /** Nullable in the live data (legacy funnel rows) — display via getDisplayName. */
  name: string | null;
  alias: string | null;
  email: string;
  phoneNumber: string | null;
  businessName?: string | null;
  title?: string | null;
  archivedAt: string | null;
  createdAt?: string;
  primaryClientId: string | null;
  clients?: MembershipInline[];
  tags: CustomerTagInline[];
}

/** People list rows (the People sub-tab) — PersonRow plus server stats. */
export interface ListPerson extends PersonRow {
  createdAt: string;
  updatedAt: string;
  sessionsCount: number;
  lifetimeSpend: number;
  lastSessionAt: string | null;
  nextSessionAt: string | null;
}

/** Person detail (person profile page) — includes their bookings. */
export interface DetailPerson extends ListPerson {
  bookings: RawBooking[];
}

/** Shape returned by the paginated allClients query. */
export interface ListClient {
  id: string;
  name: string;
  alias: string | null;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  peopleCount: number;
  sessionsCount: number;
  lifetimeSpend: number;
  lastSessionAt: string | null;
  nextSessionAt: string | null;
  people: PersonRow[];
}

/** Shape returned by the Client detail query (includes bookings). */
export interface DetailClient extends ListClient {
  bookings: RawBooking[];
}

/**
 * The minimal shape the timeline builders need from the subject record —
 * satisfied by both DetailCustomer and DetailClient.
 */
export interface TimelineSubject {
  id: string;
  createdAt: string;
  archivedAt: string | null;
}

// ── Output types ────────────────────────────────────────────────────

export type ClientStatus = "active" | "lead" | "archived" | "blocked";

// ── Status display maps ────────────────────────────────────────────

export const CLIENT_STATUS_LABEL: Record<ClientStatus, string> = {
  active: "Active",
  lead: "Lead",
  blocked: "Blocked",
  archived: "Archived",
};

export const CLIENT_STATUS_COLOR: Record<
  ClientStatus,
  { text: string; dot: string }
> = {
  active: { text: "text-sage", dot: "bg-sage" },
  lead: { text: "text-spark", dot: "bg-spark" },
  blocked: { text: "text-rust", dot: "bg-rust" },
  archived: { text: "text-ink-4", dot: "bg-ink-5" },
};

// ── Currency formatting ────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Format a numeric dollar amount as a locale-safe USD string.
 * e.g. 1234 => "$1,234"
 */
export function formatCurrency(amount: number): string {
  return currencyFormatter.format(amount);
}

export interface TimelineEntry {
  id: string;
  type: "session" | "created";
  title: string;
  detail: string;
  date: string; // ISO for sorting
  dotColor: string;
}

// ── Timeline construction ──────────────────────────────────────────

/**
 * Build a chronological timeline from a single customer's nested bookings.
 * Payment entries are omitted in v2 — lifetime spend is a server-side stat
 * and individual payment rows are not fetched for the detail pane.
 */
export function deriveTimeline(
  customer: TimelineSubject,
  bookings: RawBooking[],
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // Booking entries
  for (const b of bookings) {
    if (b.deletedAt != null) continue;
    const typeName = b.bookingType?.name ?? "Session";

    entries.push({
      id: `booking-${b.id}`,
      type: "session",
      title: "Session scheduled",
      detail: typeName,
      date: b.createdAt,
      dotColor: "bg-tide",
    });
  }

  // Client created event
  entries.push({
    id: `created-${customer.id}`,
    type: "created",
    title: "Client created",
    detail: "Intake completed",
    date: customer.createdAt,
    dotColor: "bg-ink-5",
  });

  // Sort newest first
  entries.sort((a, b) => {
    const da = new Date(a.date).getTime();
    const db = new Date(b.date).getTime();
    return db - da;
  });

  return entries;
}

// ── Activity timeline (v3 — unified feed) ─────────────────────────

/**
 * Event categories for filtering. "lifecycle" events (created, archived)
 * are always shown under "All" but have no dedicated filter chip.
 */
export type ActivityCategory =
  | "sessions"
  | "notes"
  | "payments"
  | "agreements"
  | "lifecycle";

export interface ActivityEvent {
  id: string;
  category: ActivityCategory;
  title: string;
  detail: string;
  /** ISO 8601 string, used for sorting and display */
  timestamp: string;
  /** Lucide icon name — rendered by the component */
  icon:
    | "Calendar"
    | "XCircle"
    | "CheckCircle"
    | "MessageSquare"
    | "UserPlus"
    | "Archive"
    | "CreditCard"
    | "AlertCircle"
    | "RotateCcw"
    | "ArrowRightLeft"
    | "FileCheck";
  /** Optional navigation target */
  linkTo?: string;
  /** For note events: the full note body */
  noteBody?: string;
  /** For note events: the note ID (for edit/delete) */
  noteId?: string;
  /** For note events: the author's user ID */
  noteAuthorId?: string;
  /** For note events: the author's email */
  noteAuthorEmail?: string;
  /** For note events: whether the note has been edited */
  noteEdited?: boolean;
  /** For note events on a multi-contact Client: which contact it's about */
  noteContactName?: string;
  /** Whether this event should render muted (e.g. auth-cancel releases) */
  muted?: boolean;
  /** For events tied to a specific booking (e.g. signature): the raw booking ID */
  bookingId?: string;
}

export interface ActivityDay {
  /** Display label: "Today", "Yesterday", or formatted date */
  label: string;
  /** ISO date key for dedup: "2026-05-26" */
  dateKey: string;
  events: ActivityEvent[];
}

export interface ClientNote {
  id: string;
  personId: string;
  authorId: string;
  body: string;
  authorEmail: string;
  createdAt: string;
  updatedAt: string;
}

/** Shape of a payment record from allPayments query */
export interface RawPayment {
  id: string;
  bookingId: string | null;
  type: string | null;
  state: string | null;
  amount: number | null;
  createdAt: string;
}

/** Shape of a booking move record from allBookingMoves query */
export interface RawBookingMove {
  id: string;
  bookingId: string;
  fromAvailabilityId: string;
  toAvailabilityId: string;
  state: string;
  reason: string | null;
  createdAt: string;
  toAvailability: {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
  } | null;
}

/** Shape of a booking closure record from allBookingClosures query */
export interface RawBookingClosure {
  id: string;
  bookingId: string;
  totalMinutes: number;
  totalAmount: number;
  createdAt: string;
}

/** Shape of a booking signature record from allBookingSignatures query */
export interface RawBookingSignature {
  id: string;
  bookingId: string;
  legalName: string;
  signedAt: string;
  createdAt: string;
}

/**
 * Merge bookings, notes, payments, moves, closures, signatures, and
 * lifecycle data into a unified activity timeline sorted newest-first.
 *
 * Increment 1: session-booked, session-cancelled, notes, lifecycle.
 * Increment 2: payment events (succeeded/failed/auth-cancel), session
 * moves (rescheduled), session closures (completed with duration/amount).
 * Increment 3: agreement signature events.
 */
export function deriveActivityTimeline(
  customer: TimelineSubject,
  bookings: RawBooking[],
  notes: ClientNote[],
  payments: RawPayment[] = [],
  moves: RawBookingMove[] = [],
  closures: RawBookingClosure[] = [],
  signatures: RawBookingSignature[] = [],
  contactNames?: Map<string, string>,
): ActivityEvent[] {
  const events: ActivityEvent[] = [];

  // Build a set of booking IDs that have a closure (to avoid duplicate
  // "Session completed" from both the booking state AND the closure record)
  const closedBookingIds = new Set(closures.map((c) => c.bookingId));

  // ── Session events ──────────────────────────────────────────────
  for (const b of bookings) {
    if (b.deletedAt != null) continue;

    const typeName = b.bookingType?.name ?? "Session";
    const sessionDate = b.availability
      ? formatSessionLabel(
          new Date(`${b.availability.date}T${b.availability.startTime}`),
        )
      : "Date pending";

    if (b.state === "cancelled") {
      // Cancelled: use updatedAt as the cancellation timestamp proxy
      events.push({
        id: `cancel-${b.id}`,
        category: "sessions",
        title: "Session cancelled",
        detail: typeName,
        timestamp: b.updatedAt,
        icon: "XCircle",
        linkTo: `/sessions/${b.id}`,
      });
    } else if (b.state === "completed" && !closedBookingIds.has(b.id)) {
      // Only show state-based "completed" if there is no closure record
      // (closure records produce a richer event with duration/amount)
      const completionTime = b.availability
        ? `${b.availability.date}T${b.availability.endTime}`
        : b.updatedAt;
      events.push({
        id: `complete-${b.id}`,
        category: "sessions",
        title: "Session completed",
        detail: `${typeName} · ${sessionDate}`,
        timestamp: completionTime,
        icon: "CheckCircle",
        linkTo: `/sessions/${b.id}`,
      });
    }

    // Always show the "booked" event (regardless of current state)
    events.push({
      id: `booked-${b.id}`,
      category: "sessions",
      title: "Session booked",
      detail: `${typeName} · ${sessionDate}`,
      timestamp: b.createdAt,
      icon: "Calendar",
      linkTo: `/sessions/${b.id}`,
    });
  }

  // ── Payment events ──────────────────────────────────────────────
  for (const p of payments) {
    const amount = p.amount != null ? formatCurrency(p.amount) : "";
    const isAuthCancel = p.type === "auth_cancel" && p.state === "confirmed";
    const isFailed = p.state === "failed";
    const isConfirmed =
      p.state === "confirmed" && p.type !== "auth_cancel";

    if (isAuthCancel) {
      events.push({
        id: `payment-${p.id}`,
        category: "payments",
        title: "Authorization released",
        detail: amount,
        timestamp: p.createdAt,
        icon: "RotateCcw",
        linkTo: p.bookingId ? `/sessions/${p.bookingId}` : undefined,
        muted: true,
      });
    } else if (isFailed) {
      events.push({
        id: `payment-${p.id}`,
        category: "payments",
        title: "Payment failed",
        detail: amount,
        timestamp: p.createdAt,
        icon: "AlertCircle",
        linkTo: p.bookingId ? `/sessions/${p.bookingId}` : undefined,
      });
    } else if (isConfirmed) {
      events.push({
        id: `payment-${p.id}`,
        category: "payments",
        title: "Payment received",
        detail: amount,
        timestamp: p.createdAt,
        icon: "CreditCard",
        linkTo: p.bookingId ? `/sessions/${p.bookingId}` : undefined,
      });
    }
    // Other states (pending, etc.) are intentionally omitted from the timeline
  }

  // ── Move events (session rescheduled) ───────────────────────────
  for (const m of moves) {
    const newDate = m.toAvailability
      ? formatSessionLabel(
          new Date(`${m.toAvailability.date}T${m.toAvailability.startTime}`),
        )
      : "New date pending";
    events.push({
      id: `move-${m.id}`,
      category: "sessions",
      title: "Session rescheduled",
      detail: `Moved to ${newDate}`,
      timestamp: m.createdAt,
      icon: "ArrowRightLeft",
      linkTo: `/sessions/${m.bookingId}`,
    });
  }

  // ── Closure events (session completed with duration/amount) ─────
  for (const c of closures) {
    const mins = c.totalMinutes;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    const durationLabel =
      hrs > 0
        ? remMins > 0
          ? `${hrs}h ${remMins}m`
          : `${hrs}h`
        : `${mins} min`;
    events.push({
      id: `closure-${c.id}`,
      category: "sessions",
      title: "Session completed",
      detail: `${durationLabel} · ${formatCurrency(c.totalAmount)}`,
      timestamp: c.createdAt,
      icon: "CheckCircle",
      linkTo: `/sessions/${c.bookingId}`,
    });
  }

  // ── Signature events (agreement signed) ─────────────────────────
  for (const sig of signatures) {
    // Look up the booking type name for context
    const booking = bookings.find((b) => b.id === sig.bookingId);
    const typeName = booking?.bookingType?.name ?? "Session";
    events.push({
      id: `signature-${sig.id}`,
      category: "agreements",
      title: "Agreement signed",
      detail: `${sig.legalName} · ${typeName}`,
      timestamp: sig.signedAt,
      icon: "FileCheck",
      linkTo: `/sessions/${sig.bookingId}`,
      bookingId: sig.bookingId,
    });
  }

  // ── Note events ─────────────────────────────────────────────────
  for (const n of notes) {
    const preview =
      n.body.length > 80 ? `${n.body.slice(0, 80)}...` : n.body;
    events.push({
      id: `note-${n.id}`,
      category: "notes",
      title: "Note added",
      detail: preview,
      timestamp: n.createdAt,
      icon: "MessageSquare",
      noteBody: n.body,
      noteId: n.id,
      noteAuthorId: n.authorId,
      noteAuthorEmail: n.authorEmail,
      noteEdited: n.updatedAt !== n.createdAt,
      noteContactName: contactNames?.get(n.personId),
    });
  }

  // ── Lifecycle events ────────────────────────────────────────────
  events.push({
    id: `created-${customer.id}`,
    category: "lifecycle",
    title: "Client created",
    detail: "Record created",
    timestamp: customer.createdAt,
    icon: "UserPlus",
  });

  if (customer.archivedAt) {
    events.push({
      id: `archived-${customer.id}`,
      category: "lifecycle",
      title: "Client archived",
      detail: "",
      timestamp: customer.archivedAt,
      icon: "Archive",
    });
  }

  // Sort newest first (reverse chronological)
  events.sort((a, b) => {
    const da = new Date(a.timestamp).getTime();
    const db = new Date(b.timestamp).getTime();
    return db - da;
  });

  return events;
}

/**
 * Group a flat list of activity events by calendar day.
 * Returns day groups with human-friendly labels.
 */
export function groupEventsByDay(events: ActivityEvent[]): ActivityDay[] {
  const now = new Date();
  const todayKey = dateKey(now);
  const yesterdayKey = dateKey(addDays(now, -1));

  const dayMap = new Map<string, ActivityEvent[]>();

  for (const ev of events) {
    const key = dateKey(new Date(ev.timestamp));
    const existing = dayMap.get(key);
    if (existing) {
      existing.push(ev);
    } else {
      dayMap.set(key, [ev]);
    }
  }

  const days: ActivityDay[] = [];
  for (const [key, dayEvents] of dayMap) {
    let label: string;
    if (key === todayKey) {
      label = "Today";
    } else if (key === yesterdayKey) {
      label = "Yesterday";
    } else {
      const d = new Date(key + "T12:00:00"); // noon to avoid timezone edge
      label = new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(d);
    }
    days.push({ label, dateKey: key, events: dayEvents });
  }

  return days;
}

/**
 * Count events per category for filter chip badges.
 */
export function countByCategory(
  events: ActivityEvent[],
): Record<ActivityCategory, number> {
  const counts: Record<ActivityCategory, number> = {
    sessions: 0,
    notes: 0,
    payments: 0,
    agreements: 0,
    lifecycle: 0,
  };
  for (const ev of events) {
    counts[ev.category]++;
  }
  return counts;
}

/**
 * Derive a display status from server-side data.
 * archived: has archivedAt. active: 1+ sessions. lead: 0 sessions.
 */
export function deriveStatus(
  subject: { archivedAt: string | null; sessionsCount: number },
): ClientStatus {
  if (subject.archivedAt) return "archived";
  return subject.sessionsCount > 0 ? "active" : "lead";
}


/**
 * Union of a Client's members' tags, deduplicated by slug. Tags stay
 * per-person in the data model; the client list shows the merged set.
 */
export function clientTags(
  client: Pick<ListClient, "people">,
): CustomerTagInline[] {
  const seen = new Set<string>();
  const out: CustomerTagInline[] = [];
  for (const c of client.people ?? []) {
    for (const t of c.tags ?? []) {
      if (seen.has(t.slug)) continue;
      seen.add(t.slug);
      out.push(t);
    }
  }
  return out;
}

/**
 * The person whose details represent the Client in compact UI (list rows,
 * quick-view header): the client's primary person (per-membership flag when
 * loaded, primaryClientId approximation on lean list payloads), else the
 * first non-archived member, else the first.
 */
export function primaryContact(
  client: Pick<ListClient, "people" | "id">,
): PersonRow | null {
  const people = client.people ?? [];
  return (
    people.find(
      (c) =>
        !c.archivedAt &&
        (c.clients
          ? c.clients.some((m) => m.id === client.id && m.isPrimary)
          : c.primaryClientId === client.id),
    ) ??
    people.find((c) => !c.archivedAt) ??
    people[0] ??
    null
  );
}

// ── Display name helpers ──────────────────────────────────────────

interface Nameable {
  name: string | null;
  alias: string | null;
  email?: string;
}

/**
 * The primary label for a client or person. Alias wins; legacy rows can
 * have a null name, so fall through to the email before a placeholder.
 */
export function getDisplayName(subject: Nameable): string {
  return subject.alias || subject.name || subject.email || "Unnamed";
}

/**
 * A secondary name line shown beneath the primary label.
 * Returns the real name when an alias is set (so the user can see both),
 * or null when there is no alias (nothing extra to show).
 */
export function getSecondaryName(subject: Nameable): string | null {
  return subject.alias ? subject.name : null;
}

/**
 * Initials derived from the display name (alias if set, otherwise real name).
 * "Jasmine Park" => "JP", "David" => "D".
 */
export function getInitials(subject: Nameable): string {
  const label = getDisplayName(subject);
  return label
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

/**
 * Format a session datetime (ISO) into a human-readable label, in the
 * viewer's browser timezone with a timezone label.
 * "Today · 12:00 PM PST" / "Tomorrow · 9:00 AM PST" / "May 28 · 2:00 PM PST"
 */
export function formatSessionLabel(dt: Date): string {
  const timeFmt = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

  const now = new Date();
  const todayStr = dateKey(now);
  const tomorrowStr = dateKey(addDays(now, 1));
  const dtKey = dateKey(dt);

  const dateStr =
    dtKey === todayStr
      ? "Today"
      : dtKey === tomorrowStr
        ? "Tomorrow"
        : new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
          }).format(dt);

  return `${dateStr} · ${timeFmt.format(dt)} ${timeZoneAbbr(dt)}`;
}

// ── Internal helpers ────────────────────────────────────────────────

function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
